// SPDX-License-Identifier: AGPL-3.0-or-later

import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import {Button} from '@app/features/ui/button/Button';
import {ColorPickerField} from '@app/features/ui/components/form/ColorPickerField';
import {Input} from '@app/features/ui/components/form/FormInput';
import {Spinner} from '@app/features/ui/components/Spinner';
import * as CyciProfileCommands from '@app/features/user/commands/CyciProfileCommands';
import type {
	CyciProfile,
	CyciProfileAvatarFrame,
	CyciProfileEffect,
	CyciProfileNameStyle,
	CyciProfileTheme,
} from '@app/features/user/commands/CyciProfileCommands';
import cyciFlairStyles from '@app/features/user/components/profile/CyciProfileFlair.module.css';
import cyciCardStyles from '@app/features/user/components/profile/CyciProfilePreview.module.css';
import styles from '@app/features/user/components/modals/tabs/my_profile_tab/CyciProfileSettings.module.css';
import CyciProfileFlairStore from '@app/features/user/state/CyciProfileFlair';
import Users from '@app/features/user/state/Users';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState} from 'react';

const DEFAULT_SECONDARY_COLOR = 0xa855f7;

function profilesEqual(a: CyciProfile | null, b: CyciProfile | null): boolean {
	if (!a || !b) return a === b;

	return (
		a.theme === b.theme &&
		a.secondary_color === b.secondary_color &&
		a.effect === b.effect &&
		a.avatar_frame === b.avatar_frame &&
		a.name_style === b.name_style &&
		a.featured_title === b.featured_title
	);
}

export interface CyciProfileSettingsHandle {
	/** Persists the draft if dirty. Resolves true if there was nothing to do or the save succeeded. */
	save: () => Promise<boolean>;
	/** Discards the draft, reverting to the last-saved remote profile. */
	reset: () => void;
}

interface CyciProfileSettingsProps {
	onDraftChange?: (profile: CyciProfile | null) => void;
	onDirtyChange?: (dirty: boolean) => void;
}

interface OptionCardProps {
	selected: boolean;
	label: string;
	onSelect: () => void;
	dataFlx: string;
	children: React.ReactNode;
}

function OptionCard({selected, label, onSelect, dataFlx, children}: OptionCardProps) {
	return (
		<button
			type="button"
			className={clsx(styles.optionCard, selected && styles.optionCardSelected)}
			onClick={onSelect}
			aria-pressed={selected}
			data-flx={dataFlx}
		>
			<span className={styles.optionSwatch}>{children}</span>
			<span className={styles.optionLabel}>{label}</span>
		</button>
	);
}

const THEME_OPTIONS: Array<{value: CyciProfileTheme; label: string; className?: string}> = [
	{value: 'default', label: 'Default'},
	{value: 'glass', label: 'Glass', className: cyciCardStyles.themeGlass},
	{value: 'neon', label: 'Neon', className: cyciCardStyles.themeNeon},
	{value: 'minimal', label: 'Minimal', className: cyciCardStyles.themeMinimal},
	{value: 'obsidian', label: 'Obsidian', className: cyciCardStyles.themeObsidian},
	{value: 'void', label: 'Void', className: cyciCardStyles.themeVoid},
	{value: 'midnight', label: 'Midnight', className: cyciCardStyles.themeMidnight},
	{value: 'monochrome', label: 'Monochrome', className: cyciCardStyles.themeMonochrome},
	{value: 'eclipse', label: 'Eclipse', className: cyciCardStyles.themeEclipse},
];

const EFFECT_OPTIONS: Array<{value: CyciProfileEffect; label: string; className?: string}> = [
	{value: 'none', label: 'None'},
	{value: 'glow', label: 'Glow', className: cyciCardStyles.effectGlow},
	{value: 'stars', label: 'Stars', className: cyciCardStyles.effectStars},
	{value: 'embers', label: 'Embers', className: cyciCardStyles.effectEmbers},
];

const AVATAR_FRAME_OPTIONS: Array<{value: CyciProfileAvatarFrame; label: string; className?: string}> = [
	{value: 'none', label: 'None'},
	{value: 'ring', label: 'Ring', className: cyciCardStyles.avatarRing},
	{value: 'neon', label: 'Neon', className: cyciCardStyles.avatarNeon},
	{value: 'glass', label: 'Glass', className: cyciCardStyles.avatarGlass},
];

const NAME_STYLE_OPTIONS: Array<{value: CyciProfileNameStyle; label: string; className?: string}> = [
	{value: 'default', label: 'Default'},
	{value: 'accent', label: 'Accent', className: cyciFlairStyles.nameAccent},
	{value: 'glow', label: 'Glow', className: cyciFlairStyles.nameGlow},
	{value: 'gradient', label: 'Gradient', className: cyciFlairStyles.nameGradient},
	{value: 'muted', label: 'Muted', className: cyciFlairStyles.nameMuted},
	{value: 'monochrome', label: 'Monochrome', className: cyciFlairStyles.nameMonochrome},
];

export const CyciProfileSettings = observer(
	forwardRef<CyciProfileSettingsHandle, CyciProfileSettingsProps>(function CyciProfileSettings(
		{onDraftChange, onDirtyChange},
		ref,
	) {
		const [remote, setRemote] = useState<CyciProfile | null>(null);
		const [draft, setDraft] = useState<CyciProfile | null>(null);
		const [loading, setLoading] = useState(true);
		const [saving, setSaving] = useState(false);
		const [loadError, setLoadError] = useState(false);

		const loadProfile = useCallback(async () => {
			setLoading(true);
			setLoadError(false);

			try {
				const profile = await CyciProfileCommands.fetchCurrent();
				setRemote(profile);
				setDraft(profile);
			} catch {
				setLoadError(true);
			} finally {
				setLoading(false);
			}
		}, []);

		useEffect(() => {
			void loadProfile();
		}, [loadProfile]);

		useEffect(() => {
			onDraftChange?.(draft);
		}, [draft, onDraftChange]);

		const dirty = useMemo(() => !profilesEqual(remote, draft), [remote, draft]);

		useEffect(() => {
			onDirtyChange?.(dirty);
		}, [dirty, onDirtyChange]);

		const saveProfile = useCallback(async (): Promise<boolean> => {
			if (!draft || !dirty) return true;

			setSaving(true);

			try {
				const saved = await CyciProfileCommands.updateCurrent(draft);
				setRemote(saved);
				setDraft(saved);

				if (Users.currentUserId) {
					CyciProfileFlairStore.set(Users.currentUserId, saved);
				}

				return true;
			} catch {
				return false;
			} finally {
				setSaving(false);
			}
		}, [draft, dirty]);

		const resetProfile = useCallback(() => {
			if (remote) {
				setDraft(remote);
			}
		}, [remote]);

		useImperativeHandle(ref, () => ({save: saveProfile, reset: resetProfile}), [saveProfile, resetProfile]);

		const previewColorHex = `#${(draft?.secondary_color ?? DEFAULT_SECONDARY_COLOR).toString(16).padStart(6, '0')}`;
		const previewCardStyle = {'--cyci-secondary': previewColorHex} as React.CSSProperties;
		const previewFlairStyle = {'--cyci-flair': previewColorHex} as React.CSSProperties;

		return (
			<SettingsSection
				id="cyci-profile-customization"
				title="Cyci Profile"
				description="Extended profile appearance for this Cyci instance. Saved together with your profile changes below."
				data-flx="cyci.profile.settings-section"
			>
				{loading ? (
					<div className={styles.loading}>
						<Spinner />
					</div>
				) : loadError || !draft ? (
					<div className={styles.errorState}>
						<p>Could not load your Cyci profile.</p>
						<Button type="button" variant="secondary" fitContent onClick={() => void loadProfile()}>
							Retry
						</Button>
					</div>
				) : (
					<div className={styles.container}>
						<div className={styles.optionSection}>
							<div className={styles.optionSectionHeader}>
								<span className={styles.optionSectionLabel}>Profile theme</span>
								<span className={styles.optionSectionDescription}>
									Changes the overall surface style of your profile.
								</span>
							</div>
							<div className={styles.optionGrid} data-flx="cyci.profile.theme">
								{THEME_OPTIONS.map((option) => (
									<OptionCard
										key={option.value}
										selected={draft.theme === option.value}
										label={option.label}
										onSelect={() => setDraft((current) => (current ? {...current, theme: option.value} : current))}
										dataFlx={`cyci.profile.theme.${option.value}`}
									>
										<span
											className={clsx(cyciCardStyles.cyciCard, styles.themeSwatch, option.className)}
											style={previewCardStyle}
										/>
									</OptionCard>
								))}
							</div>
						</div>

						<div className={styles.optionSection}>
							<div className={styles.optionSectionHeader}>
								<span className={styles.optionSectionLabel}>Profile effect</span>
								<span className={styles.optionSectionDescription}>
									Adds an animated or illuminated profile effect. Independent of theme.
								</span>
							</div>
							<div className={styles.optionGrid} data-flx="cyci.profile.effect">
								{EFFECT_OPTIONS.map((option) => (
									<OptionCard
										key={option.value}
										selected={draft.effect === option.value}
										label={option.label}
										onSelect={() => setDraft((current) => (current ? {...current, effect: option.value} : current))}
										dataFlx={`cyci.profile.effect.${option.value}`}
									>
										<span
											className={clsx(cyciCardStyles.cyciCard, styles.effectSwatch, option.className)}
											style={previewCardStyle}
										/>
									</OptionCard>
								))}
							</div>
						</div>

						<div className={styles.optionSection}>
							<div className={styles.optionSectionHeader}>
								<span className={styles.optionSectionLabel}>Avatar frame</span>
								<span className={styles.optionSectionDescription}>
									Changes the decorative frame around your avatar.
								</span>
							</div>
							<div className={styles.optionGrid} data-flx="cyci.profile.avatar-frame">
								{AVATAR_FRAME_OPTIONS.map((option) => (
									<OptionCard
										key={option.value}
										selected={draft.avatar_frame === option.value}
										label={option.label}
										onSelect={() =>
											setDraft((current) => (current ? {...current, avatar_frame: option.value} : current))
										}
										dataFlx={`cyci.profile.avatar-frame.${option.value}`}
									>
										<span className={clsx(styles.avatarSwatch, option.className)} style={previewFlairStyle} />
									</OptionCard>
								))}
							</div>
						</div>

						<div className={styles.optionSection}>
							<div className={styles.optionSectionHeader}>
								<span className={styles.optionSectionLabel}>Name style</span>
								<span className={styles.optionSectionDescription}>
									Colors your username in chat and profile surfaces. Independent of theme.
								</span>
							</div>
							<div className={styles.optionGrid} data-flx="cyci.profile.name-style">
								{NAME_STYLE_OPTIONS.map((option) => (
									<OptionCard
										key={option.value}
										selected={draft.name_style === option.value}
										label={option.label}
										onSelect={() =>
											setDraft((current) => (current ? {...current, name_style: option.value} : current))
										}
										dataFlx={`cyci.profile.name-style.${option.value}`}
									>
										<span className={clsx(styles.nameSwatch, option.className)} style={previewFlairStyle}>
											Aa
										</span>
									</OptionCard>
								))}
							</div>
						</div>

						<div className={styles.grid}>
							<div>
								<ColorPickerField
									label="Secondary color"
									description="Used by Cyci themes, effects, name styles, and avatar frames."
									value={draft.secondary_color ?? DEFAULT_SECONDARY_COLOR}
									defaultValue={DEFAULT_SECONDARY_COLOR}
									isDefaultValue={draft.secondary_color === null}
									onChange={(secondary_color) =>
										setDraft((current) => (current ? {...current, secondary_color} : current))
									}
									onReset={() =>
										setDraft((current) => (current ? {...current, secondary_color: null} : current))
									}
									data-flx="cyci.profile.secondary-color"
								/>
							</div>

							<div className={styles.fullWidth}>
								<Input
									label="Featured title"
									placeholder="Founder"
									maxLength={32}
									value={draft.featured_title ?? ''}
									onChange={(event) =>
										setDraft((current) =>
											current
												? {
														...current,
														featured_title: event.target.value.trimStart() || null,
													}
												: current,
										)
									}
									footer="Displayed prominently on your extended profile."
									data-flx="cyci.profile.featured-title"
								/>
							</div>
						</div>

						<div className={styles.actions}>
							{(dirty || saving) && (
								<span className={styles.statusHint} data-flx="cyci.profile.status-hint">
									{saving ? 'Saving with your profile…' : 'Unsaved — saved with your profile changes'}
								</span>
							)}

							<Button
								type="button"
								variant="secondary"
								fitContent
								disabled={!dirty || saving}
								onClick={resetProfile}
							>
								Reset
							</Button>
						</div>
					</div>
				)}
			</SettingsSection>
		);
	}),
);
